import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider, ThemeScopeBridge, useTheme } from './context/ThemeContext';
import { ConfirmDialogProvider } from './context/ConfirmDialogContext';
import MainLayout from './components/Layout/MainLayout';
import ChangePassword from './pages/auth/ChangePassword';
import { ExpressMarketCartProvider } from './pages/express-market/ExpressMarketCartContext';
import { resolveNavPermissionForPath, candidateNavKeysForPath } from './utils/rbacNavigation';
import SessionExpiredModal from './components/shared/SessionExpiredModal';
import LicenseExpiredModal from './components/shared/LicenseExpiredModal';

// Lazy-loaded pages
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const TasksPage = React.lazy(() => import('./pages/tasks/TasksPage'));
const UserDashboard = React.lazy(() => import('./pages/UserDashboard'));

// Finance
const FinanceDashboard = React.lazy(() => import('./pages/finance/FinanceDashboard'));
const ChartOfAccounts = React.lazy(() => import('./pages/finance/ChartOfAccounts'));
const JournalEntries = React.lazy(() => import('./pages/finance/JournalEntries'));
const JournalEntryForm = React.lazy(() => import('./pages/finance/JournalEntryForm'));
const TaxCodes = React.lazy(() => import('./pages/finance/TaxCodes'));
const WithholdingTypes = React.lazy(() => import('./pages/finance/WithholdingTypes'));
const WithholdingCertificates = React.lazy(() => import('./pages/finance/reports/WithholdingCertificates'));
const TransactionTypes = React.lazy(() => import('./pages/finance/TransactionTypes'));
const TrialBalance = React.lazy(() => import('./pages/finance/reports/TrialBalance'));
const DimensionAnalysis = React.lazy(() => import('./pages/finance/reports/DimensionAnalysis'));
const AccountTransactions = React.lazy(() => import('./pages/finance/reports/AccountTransactions'));
const CashAtHand = React.lazy(() => import('./pages/finance/reports/CashAtHand'));
const BalanceSheet = React.lazy(() => import('./pages/finance/reports/BalanceSheet'));
const IncomeStatement = React.lazy(() => import('./pages/finance/reports/IncomeStatement'));
const TaxReport = React.lazy(() => import('./pages/finance/reports/TaxReport'));
const KRAMonthlyLiability = React.lazy(() => import('./pages/finance/reports/KRAMonthlyLiability'));
const CashPosition = React.lazy(() => import('./pages/finance/reports/CashPosition'));
const FxRevaluation = React.lazy(() => import('./pages/finance/reports/FxRevaluation'));
const CashFlowStatement = React.lazy(() => import('./pages/finance/reports/CashFlowStatement'));
const FinanceBalanceReconciliation = React.lazy(() => import('./pages/finance/reports/FinanceBalanceReconciliation'));
const BankReconciliation = React.lazy(() => import('./pages/finance/reconciliation/BankReconciliation'));
const BankReconciliationWorkspace = React.lazy(() => import('./pages/finance/reconciliation/BankReconciliationWorkspace'));
const VatReconciliation = React.lazy(() => import('./pages/finance/vat-reconciliation/VatReconciliation'));
const VatReconciliationWorkspace = React.lazy(() => import('./pages/finance/vat-reconciliation/VatReconciliationWorkspace'));
const MonthEndClose = React.lazy(() => import('./pages/finance/MonthEndClose'));
const RecurringJournalTemplates = React.lazy(() => import('./pages/RecurringJournalTemplates'));
const AccrualsDeferrals = React.lazy(() => import('./pages/AccrualsDeferrals'));

// Banking sub-module
const BankingDashboard    = React.lazy(() => import('./pages/finance/banking/BankingDashboard'));
const BankAccounts        = React.lazy(() => import('./pages/finance/banking/BankAccounts'));
const IncomingPayments    = React.lazy(() => import('./pages/finance/banking/IncomingPayments'));
const OutgoingPayments    = React.lazy(() => import('./pages/finance/banking/OutgoingPayments'));
const BankTransfers       = React.lazy(() => import('./pages/finance/banking/BankTransfers'));
const BankingCashPosition = React.lazy(() => import('./pages/finance/banking/CashPositionForecast'));

// Customers
const CustomerList = React.lazy(() => import('./pages/customers/CustomerList'));
const CustomerGroups = React.lazy(() => import('./pages/customers/CustomerGroups'));
const CustomerCities = React.lazy(() => import('./pages/customers/CustomerCities'));
const CustomerAreas = React.lazy(() => import('./pages/customers/CustomerAreas'));
const SalesReps = React.lazy(() => import('./pages/customers/SalesReps'));
const CustomerReceipts = React.lazy(() => import('./pages/customers/CustomerReceipts'));
const CustomerReceiptForm = React.lazy(() => import('./pages/customers/CustomerReceiptForm'));
const CustomerAdjustments = React.lazy(() => import('./pages/customers/CustomerAdjustments'));
const VolumeDiscounts = React.lazy(() => import('./pages/customers/VolumeDiscounts'));
const JointBusinessPlans = React.lazy(() => import('./pages/customers/JointBusinessPlans'));
const JointBusinessPlanForm = React.lazy(() => import('./pages/customers/JointBusinessPlanForm'));
const JointBusinessPlanDetail = React.lazy(() => import('./pages/customers/JointBusinessPlanDetail'));
const JointBusinessPlanSetup = React.lazy(() => import('./pages/customers/JointBusinessPlanSetup'));
const JointBusinessPlanReviewReport = React.lazy(() => import('./pages/customers/JointBusinessPlanReviewReport'));
const CustomerAging = React.lazy(() => import('./pages/customers/reports/CustomerAging'));
const CustomerStatements = React.lazy(() => import('./pages/customers/reports/CustomerStatements'));
const CustomerTransactionEnquiry = React.lazy(() => import('./pages/customers/reports/CustomerTransactionEnquiry'));
const SalesRepCommission = React.lazy(() => import('./pages/customers/reports/SalesRepCommission'));
const CustomerBalanceReconciliation = React.lazy(() => import('./pages/customers/reports/CustomerBalanceReconciliation'));
const VolumeDiscountAnalysis = React.lazy(() => import('./pages/customers/reports/VolumeDiscountAnalysis'));
const SalesRepReport = React.lazy(() => import('./pages/customers/reports/SalesRepReport'));

// Credit Control
const CreditControlDashboard = React.lazy(() => import('./pages/creditControl/CreditControlDashboard'));
const CreditControlOnboarding = React.lazy(() => import('./pages/creditControl/CreditControlOnboarding'));
const CreditControlReminders = React.lazy(() => import('./pages/creditControl/CreditControlReminders'));
const CreditControlPolicies = React.lazy(() => import('./pages/creditControl/CreditControlPolicies'));
const CreditControlPolicySetup = React.lazy(() => import('./pages/creditControl/CreditControlPolicySetup'));

// Suppliers
const SupplierList = React.lazy(() => import('./pages/suppliers/SupplierList'));
const SupplierGroups = React.lazy(() => import('./pages/suppliers/SupplierGroups'));
const SupplierPayments = React.lazy(() => import('./pages/suppliers/SupplierPayments'));
const SupplierPaymentForm = React.lazy(() => import('./pages/suppliers/SupplierPaymentForm'));
const SupplierAdjustments = React.lazy(() => import('./pages/suppliers/SupplierAdjustments'));
const SupplierAging = React.lazy(() => import('./pages/suppliers/reports/SupplierAging'));
const SupplierTransactionEnquiry = React.lazy(() => import('./pages/suppliers/reports/SupplierTransactionEnquiry'));
const SupplierStatements = React.lazy(() => import('./pages/suppliers/reports/SupplierStatements'));
const PaymentRun = React.lazy(() => import('./pages/suppliers/reports/PaymentRun'));
const SupplierBalanceReconciliation = React.lazy(() => import('./pages/suppliers/reports/SupplierBalanceReconciliation'));

// Inventory
const ItemList = React.lazy(() => import('./pages/inventory/ItemList'));
const ItemGroups = React.lazy(() => import('./pages/inventory/ItemGroups'));
const UnitsOfMeasure = React.lazy(() => import('./pages/inventory/UnitsOfMeasure'));
const PriceLists = React.lazy(() => import('./pages/inventory/PriceLists'));
const PriceListManage = React.lazy(() => import('./pages/inventory/PriceListManage'));
const ImportPriceLists = React.lazy(() => import('./pages/inventory/ImportPriceLists'));
const Locations = React.lazy(() => import('./pages/inventory/Locations'));
const Warehouses = React.lazy(() => import('./pages/inventory/Warehouses'));
const StockTransfers = React.lazy(() => import('./pages/inventory/StockTransfers'));
const StockTransferForm = React.lazy(() => import('./pages/inventory/StockTransferForm'));
const InTransitTransferIssue = React.lazy(() => import('./pages/inventory/InTransitTransferIssue'));
const InTransitTransferReceive = React.lazy(() => import('./pages/inventory/InTransitTransferReceive'));
const InTransitTransferForm = React.lazy(() => import('./pages/inventory/InTransitTransferForm'));
const InTransitTransferDetail = React.lazy(() => import('./pages/inventory/InTransitTransferDetail'));
const StockAdjustments = React.lazy(() => import('./pages/inventory/StockAdjustments'));
const CountPlanning = React.lazy(() => import('./pages/inventory/CountPlanning'));
const CountDetail = React.lazy(() => import('./pages/inventory/CountDetail'));
const TransactionEnquiry = React.lazy(() => import('./pages/inventory/TransactionEnquiry'));
const WarehouseStock = React.lazy(() => import('./pages/inventory/reports/WarehouseStock'));
const PricingReport = React.lazy(() => import('./pages/inventory/reports/PricingReport'));
const CostingReport = React.lazy(() => import('./pages/inventory/reports/CostingReport'));
const InventoryValuation = React.lazy(() => import('./pages/inventory/reports/InventoryValuation'));
const StockAging = React.lazy(() => import('./pages/inventory/reports/StockAging'));
const FIFOCosting = React.lazy(() => import('./pages/inventory/reports/FIFOCosting'));
const CountVarianceReport = React.lazy(() => import('./pages/inventory/reports/CountVarianceReport'));
const NetSalesReport = React.lazy(() => import('./pages/inventory/reports/NetSalesReport'));
const MovementAnalysis = React.lazy(() => import('./pages/inventory/reports/MovementAnalysis'));
const InventoryTurnover = React.lazy(() => import('./pages/inventory/reports/InventoryTurnover'));
const GrossMarginReport = React.lazy(() => import('./pages/inventory/reports/GrossMarginReport'));
const SupplierPerformance = React.lazy(() => import('./pages/inventory/reports/SupplierPerformance'));
const SalesVsPurchasesReport = React.lazy(() => import('./pages/inventory/reports/SalesVsPurchasesReport'));
const BatchManagement = React.lazy(() => import('./pages/inventory/BatchManagement'));
const TraceExplorer = React.lazy(() => import('./pages/inventory/TraceExplorer'));
const LotStockOnHand = React.lazy(() => import('./pages/inventory/reports/LotStockOnHand'));
const ExpiryAging = React.lazy(() => import('./pages/inventory/reports/ExpiryAging'));
const VariantAttributes = React.lazy(() => import('./pages/inventory/VariantAttributes'));
const ReorderSuggestions = React.lazy(() => import('./pages/inventory/ReorderSuggestions'));
const Settings = React.lazy(() => import('./pages/Settings'));

// Order Entry
const SalesInvoices = React.lazy(() => import('./pages/orders/SalesInvoices'));
const SalesInvoiceForm = React.lazy(() => import('./pages/orders/SalesInvoiceForm'));
const CreditNotes = React.lazy(() => import('./pages/orders/CreditNotes'));
const CreditNoteForm = React.lazy(() => import('./pages/orders/CreditNoteForm'));
const GRVList = React.lazy(() => import('./pages/orders/GRVList'));
const GRVForm = React.lazy(() => import('./pages/orders/GRVForm'));
const AgriIntakeList = React.lazy(() => import('./pages/agri/IntakeList'));
const AgriFarms = React.lazy(() => import('./pages/agri/Farms'));
const AgriCertifications = React.lazy(() => import('./pages/agri/Certifications'));
const AgriSettlement = React.lazy(() => import('./pages/agri/Settlement'));
const AgriConsignments = React.lazy(() => import('./pages/agri/Consignments'));
const RequisitionList = React.lazy(() => import('./pages/procurement/RequisitionList'));
const RequisitionForm = React.lazy(() => import('./pages/procurement/RequisitionForm'));
const RFQList = React.lazy(() => import('./pages/procurement/RFQList'));
const RFQForm = React.lazy(() => import('./pages/procurement/RFQForm'));
const POApprovalQueue = React.lazy(() => import('./pages/procurement/POApprovalQueue'));
const ProcurementSettings = React.lazy(() => import('./pages/procurement/ProcurementSettings'));
const SupplierScorecard = React.lazy(() => import('./pages/procurement/SupplierScorecard'));
const RTSList = React.lazy(() => import('./pages/orders/RTSList'));
const RTSForm = React.lazy(() => import('./pages/orders/RTSForm'));
const SalesOrderReport = React.lazy(() => import('./pages/orders/reports/SalesOrderReport'));
const CreditNotesReport = React.lazy(() => import('./pages/orders/reports/CreditNotesReport'));
const PurchaseOrderReport = React.lazy(() => import('./pages/orders/reports/PurchaseOrderReport'));
const GRVReport = React.lazy(() => import('./pages/orders/reports/GRVReport'));
const ReturnToSupplierReport = React.lazy(() => import('./pages/orders/reports/ReturnToSupplierReport'));
const SupplierInvoiceReport = React.lazy(() => import('./pages/orders/reports/SupplierInvoiceReport'));
const DeliveryScheduleReport = React.lazy(() => import('./pages/orders/reports/DeliveryScheduleReport'));
const OpenOrdersAgingReport = React.lazy(() => import('./pages/orders/reports/OpenOrdersAgingReport'));
const BackorderFillRateReport = React.lazy(() => import('./pages/orders/reports/BackorderFillRateReport'));
const OrderToInvoiceCycleReport = React.lazy(() => import('./pages/orders/reports/OrderToInvoiceCycleReport'));
const CancellationReturnAnalysisReport = React.lazy(() => import('./pages/orders/reports/CancellationReturnAnalysisReport'));
const GrossMarginByOrderReport = React.lazy(() => import('./pages/orders/reports/GrossMarginByOrderReport'));
const PoGrvVarianceReport = React.lazy(() => import('./pages/orders/reports/PoGrvVarianceReport'));
const OnTimePerformanceReport = React.lazy(() => import('./pages/orders/reports/OnTimePerformanceReport'));

// Project Management (feature-flag gated)
const ProjectList = React.lazy(() => import('./pages/projects/ProjectList'));
const ProjectForm = React.lazy(() => import('./pages/projects/ProjectForm'));
const ProjectDetail = React.lazy(() => import('./pages/projects/ProjectDetail'));
const ResourceList = React.lazy(() => import('./pages/projects/ResourceList'));
const ProjectReportsIndex = React.lazy(() => import('./pages/projects/reports/ProjectReportsIndex'));
const ProjectStatusReport = React.lazy(() => import('./pages/projects/reports/ProjectStatusReport'));
const ProjectBudgetBillingReport = React.lazy(() => import('./pages/projects/reports/ProjectBudgetBillingReport'));
const ProjectTaskProgressReport = React.lazy(() => import('./pages/projects/reports/ProjectTaskProgressReport'));
const ProjectMilestoneTrackingReport = React.lazy(() => import('./pages/projects/reports/ProjectMilestoneTrackingReport'));
const ProjectResourceAllocationReport = React.lazy(() => import('./pages/projects/reports/ProjectResourceAllocationReport'));
const ProjectPlannedVsActualReport = React.lazy(() => import('./pages/projects/reports/ProjectPlannedVsActualReport'));
const FeatureGate = React.lazy(() => import('./components/shared/FeatureGate'));

// Manufacturing (feature-flag gated)
const BOMList = React.lazy(() => import('./pages/manufacturing/BOMList'));
const BOMForm = React.lazy(() => import('./pages/manufacturing/BOMForm'));
const WorkOrders = React.lazy(() => import('./pages/manufacturing/WorkOrders'));
const WorkOrderForm = React.lazy(() => import('./pages/manufacturing/WorkOrderForm'));
const WIPReport = React.lazy(() => import('./pages/manufacturing/reports/WIPReport'));
const ScrapReport = React.lazy(() => import('./pages/manufacturing/reports/ScrapReport'));
const BOMCostingReport = React.lazy(() => import('./pages/manufacturing/reports/BOMCostingReport'));
const TraceabilityReport = React.lazy(() => import('./pages/manufacturing/reports/TraceabilityReport'));
const ProductionCostReport = React.lazy(() => import('./pages/manufacturing/reports/ProductionCostReport'));
const MaterialUsageReport = React.lazy(() => import('./pages/manufacturing/reports/MaterialUsageReport'));
const QCReport = React.lazy(() => import('./pages/manufacturing/reports/QCReport'));
const ReplenishmentSuggestions = React.lazy(() => import('./pages/manufacturing/ReplenishmentSuggestions'));
const WorkCentersSetup = React.lazy(() => import('./pages/manufacturing/WorkCentersSetup'));
const QualitySetup = React.lazy(() => import('./pages/manufacturing/QualitySetup'));
const MaintenanceSetup = React.lazy(() => import('./pages/manufacturing/MaintenanceSetup'));
const TraceabilityWorkbench = React.lazy(() => import('./pages/manufacturing/TraceabilityWorkbench'));

// Q AI Report Generator
const QAIHome = React.lazy(() => import('./pages/qai/QAIHome'));
const QAIReportBuilder = React.lazy(() => import('./pages/qai/QAIReportBuilder'));
const QAIReportViewer = React.lazy(() => import('./pages/qai/QAIReportViewer'));
const QAIReportEdit = React.lazy(() => import('./pages/qai/QAIReportEdit'));
// Q AI Dashboard Generator
const QAIDashboardHome = React.lazy(() => import('./pages/qai/QAIDashboardHome'));
const QAIDashboardBuilder = React.lazy(() => import('./pages/qai/QAIDashboardBuilder'));
const QAIDashboardViewer = React.lazy(() => import('./pages/qai/QAIDashboardViewer'));
// Auth & Registration
const WelcomePage         = React.lazy(() => import('./pages/auth/WelcomePage'));
const ForgotPassword      = React.lazy(() => import('./pages/auth/ForgotPassword'));
const ResetPassword       = React.lazy(() => import('./pages/auth/ResetPassword'));
const RegistrationWizard  = React.lazy(() => import('./pages/registration/RegistrationWizard'));
const ProvisioningScreen  = React.lazy(() => import('./pages/registration/ProvisioningScreen'));

// Delivery Management (feature-flag gated)
const DeliveryDrivers = React.lazy(() => import('./pages/delivery/Drivers'));
const DeliveryVehicles = React.lazy(() => import('./pages/delivery/Vehicles'));
const DeliveryZones = React.lazy(() => import('./pages/delivery/Zones'));
const DeliveryReturnReasons = React.lazy(() => import('./pages/delivery/ReturnReasons'));
const DeliveryManifests = React.lazy(() => import('./pages/delivery/ManifestList'));
const DeliveryManifestForm = React.lazy(() => import('./pages/delivery/ManifestForm'));
const DeliveryReturnQueue = React.lazy(() => import('./pages/delivery/ReturnQueue'));
const MyDeliveries = React.lazy(() => import('./pages/delivery/MyDeliveries'));
const DeliveryEfficiency  = React.lazy(() => import('./pages/delivery/DeliveryEfficiency'));
const ManifestThroughputReport = React.lazy(() => import('./pages/delivery/ManifestThroughputReport'));
const DriverPerformanceReport = React.lazy(() => import('./pages/delivery/DriverPerformanceReport'));
const ReturnAnalyticsReport = React.lazy(() => import('./pages/delivery/ReturnAnalyticsReport'));
const SlaBreachReport = React.lazy(() => import('./pages/delivery/SlaBreachReport'));
const ZoneUtilizationReport = React.lazy(() => import('./pages/delivery/ZoneUtilizationReport'));
const ReissueCycleReport = React.lazy(() => import('./pages/delivery/ReissueCycleReport'));

// CRM (feature-flag gated)
const CrmContactsList = React.lazy(() => import('./pages/crm/ContactsList'));
const CrmContactDetail = React.lazy(() => import('./pages/crm/ContactDetail'));
const CrmOpportunities = React.lazy(() => import('./pages/crm/Opportunities'));
const CrmSalesPipeline = React.lazy(() => import('./pages/crm/SalesPipeline'));
const CrmPipelineSettings = React.lazy(() => import('./pages/crm/PipelineSettings'));
const CrmDashboard = React.lazy(() => import('./pages/crm/CrmDashboard'));
const CrmSalesExecDashboard = React.lazy(() => import('./pages/crm/SalesExecDashboard'));
const CrmCustomer360 = React.lazy(() => import('./pages/crm/Customer360'));
const CrmWorkflowRules = React.lazy(() => import('./pages/crm/WorkflowRules'));
const CrmLeadsDiscoveryTemplates = React.lazy(() => import('./pages/crm/LeadsDiscoveryTemplates'));
const CrmLeadReviewQueue = React.lazy(() => import('./pages/crm/LeadReviewQueue'));
const CrmActivitiesGlobal = React.lazy(() => import('./pages/crm/ActivitiesGlobal'));
const CrmOptOuts = React.lazy(() => import('./pages/crm/OptOuts'));
const CrmAccountsAtRiskReport = React.lazy(() => import('./pages/crm/AccountsAtRiskReport'));
const CrmMailboxInbox = React.lazy(() => import('./pages/crm/MailboxInbox'));
const POSHome = React.lazy(() => import('./pages/pos/POSHome'));
const HQDashboard = React.lazy(() => import('./pages/pos/HQDashboard'));
const ExpressMarketHome = React.lazy(() => import('./pages/express-market/ExpressMarketHome'));
const ExpressMarketSearch = React.lazy(() => import('./pages/express-market/ExpressMarketSearch'));
const ExpressMarketProduct = React.lazy(() => import('./pages/express-market/ExpressMarketProduct'));
const ExpressMarketCart = React.lazy(() => import('./pages/express-market/ExpressMarketCart'));
const ExpressMarketCheckout = React.lazy(() => import('./pages/express-market/ExpressMarketCheckout'));
const ExpressMarketMyOrders = React.lazy(() => import('./pages/express-market/ExpressMarketMyOrders'));
const ExpressMarketOrderDetail = React.lazy(() => import('./pages/express-market/ExpressMarketOrderDetail'));
const ExpressMarketAdmin = React.lazy(() => import('./pages/express-market/ExpressMarketAdmin'));
const POSCheckout = React.lazy(() => import('./pages/pos/POSCheckout'));
const POSSessions = React.lazy(() => import('./pages/pos/POSSessions'));
const POSSetup = React.lazy(() => import('./pages/pos/POSSetup'));
const SalesByTerminalPaymentReport = React.lazy(() => import('./pages/pos/reports/SalesByTerminalPaymentReport'));
const SalesByInventoryItemReport = React.lazy(() => import('./pages/pos/reports/SalesByInventoryItemReport'));
const SessionZReport = React.lazy(() => import('./pages/pos/reports/SessionZReport'));
const Forbidden = () => <div className="p-8 text-center text-gray-600">You do not have permission to access this page.</div>;

const CHUNK_RELOAD_KEY = 'journeyai:chunk-reload';

class ChunkLoadBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: '' };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      message: error?.message || 'A page resource failed to load.',
    };
  }

  componentDidCatch(error) {
    const message = String(error?.message || '');
    const isChunkError = error?.name === 'ChunkLoadError'
      || message.includes('Loading chunk')
      || message.includes('ChunkLoadError');

    if (!isChunkError) return;

    const lastReloadKey = `${window.location.pathname}${window.location.search}`;
    if (window.sessionStorage.getItem(CHUNK_RELOAD_KEY) === lastReloadKey) {
      return;
    }

    window.sessionStorage.setItem(CHUNK_RELOAD_KEY, lastReloadKey);
    window.location.reload();
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-[16rem] flex flex-col items-center justify-center gap-4 text-center">
          <div className="text-sm font-medium text-gray-700">A page resource failed to load.</div>
          <div className="max-w-md text-xs text-gray-500">{this.state.message}</div>
          <button
            type="button"
            onClick={() => {
              window.sessionStorage.removeItem(CHUNK_RELOAD_KEY);
              window.location.reload();
            }}
            className="px-4 py-2 rounded-lg bg-emerald-500 text-white hover:bg-emerald-600"
          >
            Reload page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }
  if (isAuthenticated && user?.must_change_password && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }
  return isAuthenticated ? children : <Navigate to="/login" />;
};

const SuspenseWrapper = ({ children }) => (
  <ChunkLoadBoundary>
    <React.Suspense
      fallback={
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
        </div>
      }
    >
      {children}
    </React.Suspense>
  </ChunkLoadBoundary>
);

const RbacRouteGate = ({ children }) => {
  const location = useLocation();
  const { permissionsLoading, permissionSnapshot, can, isNavHidden } = useAuth();
  const requiredPermission = resolveNavPermissionForPath(location.pathname);
  const navHidden = candidateNavKeysForPath(location.pathname).some((k) => isNavHidden(k));

  if (!requiredPermission && !navHidden) {
    return children;
  }

  // Only block rendering on the very first permissions fetch (no snapshot yet).
  // Background stale-cache refreshes run with permissionsLoading=true while
  // permissionSnapshot already holds valid data — rendering a spinner in that
  // case unmounts the current page and wipes any unsaved form state.
  if (permissionsLoading && !permissionSnapshot) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (navHidden || (requiredPermission && !can(requiredPermission))) {
    return <Forbidden />;
  }

  return children;
};

const LaunchPageHome = () => {
  const { user } = useAuth();
  const raw = String(user?.launch_page || '').trim();

  // A generated Q AI dashboard can be a user's landing page: 'qai:<id>'.
  const qaiMatch = raw.match(/^qai:(\d+)$/i);
  if (qaiMatch) {
    return (
      <FeatureGate flag="q_ai_dashboard_generator">
        <QAIDashboardViewer dashboardId={qaiMatch[1]} embedded />
      </FeatureGate>
    );
  }

  // A driver's home IS their run. Without this they would land on a dashboard
  // whose every tile they lack permission to open.
  if (raw.toLowerCase() === 'delivery_driver') return <Navigate to="/my-deliveries" replace />;

  // A module dashboard as the landing page REDIRECTS rather than rendering
  // inline. '/' resolves to nav.dashboard in PATH_PERMISSION_RULES, so mounting
  // <FinanceDashboard /> here would render a page whose API then 403s for a
  // user without nav.finance.dashboard, and would leave the sidebar
  // highlighting Dashboard instead of the module. Redirecting hands the URL to
  // RbacRouteGate with the right key. Same reasoning as delivery_driver above.
  // /crm/dashboard routes to CrmDashboardHome, so the user's existing
  // manager/sales_exec preference is still honoured.
  if (raw.toLowerCase() === 'crm') return <Navigate to="/crm/dashboard" replace />;
  if (raw.toLowerCase() === 'finance') return <Navigate to="/finance/dashboard" replace />;

  return raw.toLowerCase() === 'user_based' ? <UserDashboard /> : <Dashboard />;
};

const CrmDashboardHome = () => {
  const { user } = useAuth();
  const pref = String(user?.crm_dashboard || '').trim().toLowerCase();
  return pref === 'sales_exec' ? <CrmSalesExecDashboard /> : <CrmDashboard />;
};

/**
 * Tells ThemeContext who is signed in so the theme preference is stored per
 * user rather than per browser - shared POS tills otherwise carry one
 * operator's choice into the next operator's shift. Scoped by tenant as well
 * as user id, because user ids are only unique within a tenant.
 */
const ThemeScopeUser = () => {
  const { user } = useAuth();
  const scopeId = user?.id
    ? `${user.company_code || user.tenantDb || 'default'}:${user.id}`
    : null;
  return <ThemeScopeBridge userId={scopeId} />;
};

const ThemedToaster = () => {
  const { isDark } = useTheme();

  return (
    <Toaster
      position="top-right"
      toastOptions={{
        duration: 3000,
        style: {
          background: isDark ? '#111827' : '#0A2540',
          color: '#fff',
          fontSize: '14px',
          border: isDark ? '1px solid #374151' : '1px solid transparent',
        },
        success: {
          iconTheme: { primary: '#00C49A', secondary: '#fff' },
        },
      }}
    />
  );
};

const AppRoutes = () => {
  const { isAuthenticated } = useAuth();

  return (
    <Routes>
      {/* Public auth & registration routes */}
      <Route
        path="/login"
        element={
          isAuthenticated ? <Navigate to="/" replace /> : (
            <SuspenseWrapper>
              <WelcomePage />
            </SuspenseWrapper>
          )
        }
      />

      <Route
        path="/forgot-password"
        element={
          <SuspenseWrapper>
            <ForgotPassword />
          </SuspenseWrapper>
        }
      />

      <Route
        path="/reset-password"
        element={
          <SuspenseWrapper>
            <ResetPassword />
          </SuspenseWrapper>
        }
      />

      <Route
        path="/register"
        element={
          <SuspenseWrapper>
            <RegistrationWizard />
          </SuspenseWrapper>
        }
      />

      <Route
        path="/register/provisioning"
        element={
          <SuspenseWrapper>
            <ProvisioningScreen />
          </SuspenseWrapper>
        }
      />

      <Route
        path="/change-password"
        element={
          <ProtectedRoute>
            <ChangePassword />
          </ProtectedRoute>
        }
      />

      {/* Protected routes */}
      <Route
        path="/*"
        element={
          <ProtectedRoute>
              <MainLayout>
                <SuspenseWrapper>
                  <RbacRouteGate>
                    <Routes>
                  <Route path="/" element={<LaunchPageHome />} />
                  <Route path="/company-dashboard" element={<Dashboard />} />
                  <Route path="/user-dashboard" element={<UserDashboard />} />
                  <Route path="/tasks" element={<TasksPage />} />

                  {/* Finance */}
                  <Route path="/finance/dashboard" element={<FinanceDashboard />} />
                  <Route path="/finance/accounts" element={<ChartOfAccounts />} />
                  <Route path="/finance/journals" element={<JournalEntries />} />
                  <Route path="/finance/journals/new" element={<JournalEntryForm />} />
                  <Route path="/finance/journals/:id" element={<JournalEntryForm />} />
                  <Route path="/finance/tax-codes" element={<TaxCodes />} />
                  <Route path="/finance/withholding-types" element={<WithholdingTypes />} />
                  <Route path="/finance/reports/withholding-register" element={<WithholdingCertificates />} />
                  <Route path="/finance/transaction-types" element={<TransactionTypes />} />
                  <Route path="/finance/reports/trial-balance" element={<TrialBalance />} />
                  <Route path="/finance/reports/dimension-analysis" element={<DimensionAnalysis />} />
                  <Route path="/finance/reports/account-transactions" element={<AccountTransactions />} />
                  <Route path="/finance/reports/cash-at-hand" element={<CashAtHand />} />
                  <Route path="/finance/reports/balance-sheet" element={<BalanceSheet />} />
                  <Route path="/finance/reports/income-statement" element={<IncomeStatement />} />
                  <Route path="/finance/reports/cash-position" element={<CashPosition />} />
                  <Route path="/finance/reports/cash-flow" element={<CashFlowStatement />} />
                  <Route path="/finance/reports/tax-report" element={<TaxReport />} />
                  <Route path="/finance/reports/kra-monthly" element={<KRAMonthlyLiability />} />
                  <Route path="/finance/reports/fx-revaluation" element={<FxRevaluation />} />
                  <Route path="/finance/reports/balance-reconciliation" element={<FinanceBalanceReconciliation />} />
                  <Route path="/finance/reconciliation" element={<BankReconciliation />} />
                  <Route path="/finance/reconciliation/:id" element={<BankReconciliationWorkspace />} />
                  <Route path="/finance/vat-reconciliation" element={<VatReconciliation />} />
                  <Route path="/finance/vat-reconciliation/:id" element={<VatReconciliationWorkspace />} />
                  <Route path="/finance/month-end-close" element={<MonthEndClose />} />
                  <Route path="/finance/recurring-templates" element={<RecurringJournalTemplates />} />
                  <Route path="/finance/accruals" element={<AccrualsDeferrals />} />

                  {/* Banking */}
                  <Route path="/finance/banking" element={<BankingDashboard />} />
                  <Route path="/finance/banking/accounts" element={<BankAccounts />} />
                  <Route path="/finance/banking/incoming-payments" element={<IncomingPayments />} />
                  <Route path="/finance/banking/outgoing-payments" element={<OutgoingPayments />} />
                  <Route path="/finance/banking/transfers" element={<BankTransfers />} />
                  <Route path="/finance/banking/cash-position" element={<BankingCashPosition />} />

                  {/* Customers */}
                  <Route path="/customers" element={<CustomerList />} />
                  <Route path="/customers/groups" element={<CustomerGroups />} />
                  <Route path="/customers/cities" element={<CustomerCities />} />
                  <Route path="/customers/areas" element={<CustomerAreas />} />
                  <Route path="/customers/sales-reps" element={<SalesReps />} />
                  <Route path="/customers/receipts" element={<CustomerReceipts />} />
                  <Route path="/customers/receipts/new" element={<CustomerReceiptForm />} />
                  <Route path="/customers/receipts/:id" element={<CustomerReceiptForm />} />
                  <Route path="/customers/adjustments" element={<CustomerAdjustments />} />
                  <Route path="/customers/discounts" element={<VolumeDiscounts />} />
                  <Route path="/customers/jbp" element={<JointBusinessPlans />} />
                  <Route path="/customers/jbp/new" element={<JointBusinessPlanForm />} />
                  <Route path="/customers/jbp/setup" element={<JointBusinessPlanSetup />} />
                  <Route path="/customers/jbp/:id" element={<JointBusinessPlanDetail />} />
                  <Route path="/customers/jbp/:id/edit" element={<JointBusinessPlanForm />} />
                  <Route path="/customers/jbp/:id/review-report" element={<JointBusinessPlanReviewReport />} />
                  <Route path="/customers/reports/aging" element={<CustomerAging />} />
                  <Route path="/customers/reports/statements" element={<CustomerStatements />} />
                  <Route path="/customers/reports/transactions" element={<CustomerTransactionEnquiry />} />
                  <Route path="/customers/reports/sales-rep-commission" element={<SalesRepCommission />} />
                  <Route path="/customers/reports/balance-reconciliation" element={<CustomerBalanceReconciliation />} />
                  <Route path="/customers/reports/volume-discount-analysis" element={<VolumeDiscountAnalysis />} />
                  <Route path="/customers/reports/sales-rep" element={<SalesRepReport />} />

                  {/* Credit Control */}
                  <Route path="/credit-control" element={<CreditControlDashboard />} />
                  <Route path="/credit-control/onboarding" element={<CreditControlOnboarding />} />
                  <Route path="/credit-control/reminders" element={<CreditControlReminders />} />
                  <Route path="/credit-control/policies" element={<CreditControlPolicies />} />
                  <Route path="/credit-control/policy-setup" element={<CreditControlPolicySetup />} />

                  {/* Suppliers */}
                  <Route path="/suppliers" element={<SupplierList />} />
                  <Route path="/suppliers/groups" element={<SupplierGroups />} />
                  <Route path="/suppliers/payments" element={<SupplierPayments />} />
                  <Route path="/suppliers/payments/new" element={<SupplierPaymentForm />} />
                  <Route path="/suppliers/payments/:id" element={<SupplierPaymentForm />} />
                  <Route path="/suppliers/adjustments" element={<SupplierAdjustments />} />
                  <Route path="/suppliers/reports/aging" element={<SupplierAging />} />
                  <Route path="/suppliers/reports/transactions" element={<SupplierTransactionEnquiry />} />
                  <Route path="/suppliers/reports/statements" element={<SupplierStatements />} />
                  <Route path="/suppliers/reports/payment-run" element={<PaymentRun />} />
                  <Route path="/suppliers/reports/balance-reconciliation" element={<SupplierBalanceReconciliation />} />

                  {/* Inventory */}
                  <Route path="/inventory/items" element={<ItemList />} />
                  <Route path="/inventory/groups" element={<ItemGroups />} />
                  <Route path="/inventory/uom" element={<UnitsOfMeasure />} />
                  <Route path="/inventory/price-lists" element={<PriceLists />} />
                  <Route path="/inventory/price-lists/:id/manage" element={<PriceListManage />} />
                  <Route path="/inventory/import-price-lists" element={<ImportPriceLists />} />
                  <Route path="/inventory/locations" element={<Locations />} />
                  <Route path="/inventory/warehouses" element={<Warehouses />} />
                  <Route path="/inventory/transfers" element={<StockTransfers />} />
                  <Route path="/inventory/transfers/new" element={<StockTransferForm />} />
                  <Route path="/inventory/intransit-transfers" element={<Navigate to="/inventory/intransit-transfers/issue" replace />} />
                  <Route path="/inventory/intransit-transfers/issue" element={<InTransitTransferIssue />} />
                  <Route path="/inventory/intransit-transfers/receive" element={<InTransitTransferReceive />} />
                  <Route path="/inventory/intransit-transfers/new" element={<InTransitTransferForm />} />
                  <Route path="/inventory/intransit-transfers/issue/:id" element={<InTransitTransferDetail viewMode="issue" />} />
                  <Route path="/inventory/intransit-transfers/receive/:id" element={<InTransitTransferDetail viewMode="receive" />} />
                  <Route path="/inventory/intransit-transfers/:id" element={<InTransitTransferDetail />} />
                  <Route path="/inventory/adjustments" element={<StockAdjustments />} />
                  <Route path="/inventory/counts" element={<CountPlanning />} />
                  <Route path="/inventory/counts/:id" element={<CountDetail />} />
                  <Route path="/inventory/transactions" element={<TransactionEnquiry />} />
                  <Route path="/inventory/reports/transactions" element={<TransactionEnquiry />} />
                  <Route path="/inventory/reports/stock" element={<WarehouseStock />} />
                  <Route path="/inventory/reports/pricing" element={<PricingReport />} />
                  <Route path="/inventory/reports/costing" element={<CostingReport />} />
                  <Route path="/inventory/reports/net-sales" element={<NetSalesReport />} />
                  <Route path="/inventory/reports/valuation" element={<InventoryValuation />} />
                  <Route path="/inventory/reports/stock-aging" element={<StockAging />} />
                  <Route path="/inventory/reports/fifo-costing" element={<FIFOCosting />} />
                  <Route path="/inventory/reports/count-variance" element={<CountVarianceReport />} />
                  <Route path="/inventory/reports/movement-analysis" element={<MovementAnalysis />} />
                  <Route path="/inventory/reports/turnover" element={<InventoryTurnover />} />
                  <Route path="/inventory/reports/gross-margin" element={<GrossMarginReport />} />
                  <Route path="/inventory/reports/supplier-performance" element={<SupplierPerformance />} />
                  <Route path="/inventory/reports/sales-vs-purchases" element={<SalesVsPurchasesReport />} />
                  <Route path="/inventory/reports/lot-stock" element={<LotStockOnHand />} />
                  <Route path="/inventory/reports/expiry-aging" element={<ExpiryAging />} />
                  <Route path="/inventory/batches" element={<BatchManagement />} />
                  <Route path="/inventory/trace" element={<TraceExplorer />} />
                  <Route path="/inventory/trace/:batchId" element={<TraceExplorer />} />
                  <Route path="/inventory/variant-attributes" element={<VariantAttributes />} />
                  <Route path="/inventory/reorder-suggestions" element={<ReorderSuggestions />} />
                  <Route path="/inventory/feature-flags" element={<Navigate to="/settings/feature-flags" replace />} />

                  {/* Settings */}
                  <Route path="/settings" element={<Navigate to="/settings/general" replace />} />
                  <Route path="/settings/:tabKey" element={<Settings />} />

                  {/* Express Market (flag-gated) */}
                  <Route path="/express-market" element={<FeatureGate flag="express_market"><ExpressMarketHome /></FeatureGate>} />
                  <Route path="/express-market/search" element={<FeatureGate flag="express_market"><ExpressMarketSearch /></FeatureGate>} />
                  <Route path="/express-market/product/:slug" element={<FeatureGate flag="express_market"><ExpressMarketProduct /></FeatureGate>} />
                  <Route path="/express-market/cart" element={<FeatureGate flag="express_market"><ExpressMarketCart /></FeatureGate>} />
                  <Route path="/express-market/checkout" element={<FeatureGate flag="express_market"><ExpressMarketCheckout /></FeatureGate>} />
                  <Route path="/express-market/orders" element={<FeatureGate flag="express_market"><ExpressMarketMyOrders /></FeatureGate>} />
                  <Route path="/express-market/orders/:id" element={<FeatureGate flag="express_market"><ExpressMarketOrderDetail /></FeatureGate>} />
                  <Route path="/express-market/admin/*" element={<FeatureGate flag="express_market"><ExpressMarketAdmin /></FeatureGate>} />

                  {/* Order Entry */}
                  <Route path="/orders/quotes-sales-orders" element={<SalesInvoices key="quotes" view="quotes" />} />
                  <Route path="/orders/invoices" element={<SalesInvoices key="invoices" view="invoices" />} />
                  <Route path="/orders/invoices/new" element={<SalesInvoiceForm />} />
                  <Route path="/orders/invoices/:id" element={<SalesInvoiceForm />} />
                  <Route path="/orders/credit-notes" element={<CreditNotes />} />
                  <Route path="/orders/credit-notes/new" element={<CreditNoteForm />} />
                  <Route path="/orders/credit-notes/:id" element={<CreditNoteForm />} />
                  <Route path="/orders/purchase-orders" element={<GRVList />} />
                  <Route path="/orders/purchase-orders/new" element={<GRVForm />} />
                  <Route path="/orders/purchase-orders/:id" element={<GRVForm />} />
                  {/* Agri Export. Every route is wrapped in the flag gate, so a
                      tenant without the module is redirected rather than shown a
                      screen whose API calls would all 403. */}
                  <Route path="/agri/intake" element={<FeatureGate flag="agri_export"><AgriIntakeList /></FeatureGate>} />
                  <Route path="/agri/farms" element={<FeatureGate flag="agri_export"><AgriFarms /></FeatureGate>} />
                  <Route path="/agri/certifications" element={<FeatureGate flag="agri_export"><AgriCertifications /></FeatureGate>} />
                  <Route path="/agri/settlement" element={<FeatureGate flag="agri_export"><AgriSettlement /></FeatureGate>} />
                  <Route path="/agri/consignments" element={<FeatureGate flag="agri_export"><AgriConsignments /></FeatureGate>} />
                  <Route path="/procurement/requisitions" element={<FeatureGate flag="procurement"><RequisitionList /></FeatureGate>} />
                  <Route path="/procurement/requisitions/new" element={<FeatureGate flag="procurement"><RequisitionForm /></FeatureGate>} />
                  <Route path="/procurement/requisitions/:id" element={<FeatureGate flag="procurement"><RequisitionForm /></FeatureGate>} />
                  <Route path="/procurement/rfqs" element={<FeatureGate flag="procurement"><RFQList /></FeatureGate>} />
                  <Route path="/procurement/rfqs/new" element={<FeatureGate flag="procurement"><RFQForm /></FeatureGate>} />
                  <Route path="/procurement/rfqs/:id" element={<FeatureGate flag="procurement"><RFQForm /></FeatureGate>} />
                  <Route path="/procurement/po-approvals" element={<FeatureGate flag="procurement"><POApprovalQueue /></FeatureGate>} />
                  <Route path="/procurement/supplier-scorecard" element={<FeatureGate flag="procurement"><SupplierScorecard /></FeatureGate>} />
                  <Route path="/procurement/settings" element={<FeatureGate flag="procurement"><ProcurementSettings /></FeatureGate>} />
                  <Route path="/orders/rts" element={<RTSList />} />
                  <Route path="/orders/rts/new" element={<RTSForm />} />
                  <Route path="/orders/rts/:id" element={<RTSForm />} />
                  <Route path="/orders/reports/sales-order" element={<SalesOrderReport />} />
                  <Route path="/orders/reports/credit-notes" element={<CreditNotesReport />} />
                  <Route path="/orders/reports/purchase-order" element={<PurchaseOrderReport />} />
                  <Route path="/orders/reports/grv" element={<GRVReport />} />
                  <Route path="/orders/reports/supplier-invoice" element={<SupplierInvoiceReport />} />
                  <Route path="/orders/reports/return-to-supplier" element={<ReturnToSupplierReport />} />
                  <Route path="/orders/reports/delivery-schedule" element={<DeliveryScheduleReport />} />
                  <Route path="/orders/reports/open-orders-aging" element={<OpenOrdersAgingReport />} />
                  <Route path="/orders/reports/backorder-fill-rate" element={<BackorderFillRateReport />} />
                  <Route path="/orders/reports/order-to-invoice-cycle" element={<OrderToInvoiceCycleReport />} />
                  <Route path="/orders/reports/cancellation-return-analysis" element={<CancellationReturnAnalysisReport />} />
                  <Route path="/orders/reports/gross-margin-by-order" element={<GrossMarginByOrderReport />} />
                  <Route path="/orders/reports/po-grv-variance" element={<PoGrvVarianceReport />} />
                  <Route path="/orders/reports/on-time-performance" element={<OnTimePerformanceReport />} />

                  {/* POS (flag-gated) */}
                  <Route path="/pos" element={<FeatureGate flag="pos"><POSHome /></FeatureGate>} />
                  <Route path="/pos/hq-dashboard" element={<FeatureGate flag="pos"><HQDashboard /></FeatureGate>} />
                  <Route path="/pos/checkout" element={<FeatureGate flag="pos"><POSCheckout /></FeatureGate>} />
                  <Route path="/pos/sessions" element={<FeatureGate flag="pos"><POSSessions /></FeatureGate>} />
                  <Route path="/pos/setup" element={<FeatureGate flag="pos"><POSSetup /></FeatureGate>} />
                  <Route path="/pos/reports/sales-by-terminal-payment" element={<FeatureGate flag="pos"><SalesByTerminalPaymentReport /></FeatureGate>} />
                  <Route path="/pos/reports/inventory-sales" element={<FeatureGate flag="pos"><SalesByInventoryItemReport /></FeatureGate>} />
                  <Route path="/pos/reports/z-report" element={<FeatureGate flag="pos"><SessionZReport /></FeatureGate>} />

                  {/* Project Management (flag-gated) */}
                  <Route path="/projects" element={<FeatureGate flag="project_management"><ProjectList /></FeatureGate>} />
                  <Route path="/projects/new" element={<FeatureGate flag="project_management"><ProjectForm /></FeatureGate>} />
                  <Route path="/projects/resources" element={<FeatureGate flag="project_management"><ResourceList /></FeatureGate>} />
                  <Route path="/projects/reports" element={<FeatureGate flag="project_management"><ProjectReportsIndex /></FeatureGate>} />
                  <Route path="/projects/reports/status" element={<FeatureGate flag="project_management"><ProjectStatusReport /></FeatureGate>} />
                  <Route path="/projects/reports/budget-billing" element={<FeatureGate flag="project_management"><ProjectBudgetBillingReport /></FeatureGate>} />
                  <Route path="/projects/reports/tasks" element={<FeatureGate flag="project_management"><ProjectTaskProgressReport /></FeatureGate>} />
                  <Route path="/projects/reports/milestones" element={<FeatureGate flag="project_management"><ProjectMilestoneTrackingReport /></FeatureGate>} />
                  <Route path="/projects/reports/resources" element={<FeatureGate flag="project_management"><ProjectResourceAllocationReport /></FeatureGate>} />
                  <Route path="/projects/reports/planned-vs-actual" element={<FeatureGate flag="project_management"><ProjectPlannedVsActualReport /></FeatureGate>} />

                  {/* Manufacturing (flag-gated) */}
                  <Route path="/manufacturing/boms" element={<FeatureGate flag="manufacturing"><BOMList /></FeatureGate>} />
                  <Route path="/manufacturing/boms/new" element={<FeatureGate flag="manufacturing"><BOMForm /></FeatureGate>} />
                  <Route path="/manufacturing/boms/:id" element={<FeatureGate flag="manufacturing"><BOMForm /></FeatureGate>} />
                  <Route path="/manufacturing/work-orders" element={<FeatureGate flag="manufacturing"><WorkOrders /></FeatureGate>} />
                  <Route path="/manufacturing/work-orders/new" element={<FeatureGate flag="manufacturing"><WorkOrderForm /></FeatureGate>} />
                  <Route path="/manufacturing/work-orders/:id" element={<FeatureGate flag="manufacturing"><WorkOrderForm /></FeatureGate>} />
                  <Route path="/manufacturing/reports/wip" element={<FeatureGate flag="manufacturing"><WIPReport /></FeatureGate>} />
                  <Route path="/manufacturing/reports/scrap" element={<FeatureGate flag="manufacturing"><ScrapReport /></FeatureGate>} />
                  <Route path="/manufacturing/reports/bom-costing" element={<FeatureGate flag="manufacturing"><BOMCostingReport /></FeatureGate>} />
                  <Route path="/manufacturing/reports/traceability" element={<FeatureGate flag="manufacturing"><TraceabilityReport /></FeatureGate>} />
                  <Route path="/manufacturing/reports/production-cost" element={<FeatureGate flag="manufacturing"><ProductionCostReport /></FeatureGate>} />
                  <Route path="/manufacturing/reports/material-usage" element={<FeatureGate flag="manufacturing"><MaterialUsageReport /></FeatureGate>} />
                  <Route path="/manufacturing/reports/quality-checks" element={<FeatureGate flag="manufacturing"><QCReport /></FeatureGate>} />
                  <Route path="/manufacturing/replenishment" element={<FeatureGate flag="manufacturing"><ReplenishmentSuggestions /></FeatureGate>} />
                  <Route path="/manufacturing/setup/work-centers" element={<FeatureGate flag="manufacturing"><WorkCentersSetup /></FeatureGate>} />
                  <Route path="/manufacturing/setup/quality" element={<FeatureGate flag="manufacturing"><QualitySetup /></FeatureGate>} />
                  <Route path="/manufacturing/setup/maintenance" element={<FeatureGate flag="manufacturing"><MaintenanceSetup /></FeatureGate>} />
                  <Route path="/manufacturing/traceability" element={<FeatureGate flag="manufacturing"><TraceabilityWorkbench /></FeatureGate>} />
                  <Route path="/projects/:id" element={<FeatureGate flag="project_management"><ProjectDetail /></FeatureGate>} />
                  <Route path="/projects/:id/edit" element={<FeatureGate flag="project_management"><ProjectForm /></FeatureGate>} />

                  {/* Q AI Report Generator (flag-gated) */}
                  <Route path="/qai-reports" element={<FeatureGate flag="q_ai_report_generator"><QAIHome /></FeatureGate>} />
                  <Route path="/qai-reports/new" element={<FeatureGate flag="q_ai_report_generator"><QAIReportBuilder /></FeatureGate>} />
                  <Route path="/qai-reports/:id" element={<FeatureGate flag="q_ai_report_generator"><QAIReportViewer /></FeatureGate>} />
                  <Route path="/qai-reports/:id/edit" element={<FeatureGate flag="q_ai_report_generator"><QAIReportEdit /></FeatureGate>} />

                  {/* Q AI Dashboard Generator (flag-gated) */}
                  <Route path="/qai-dashboards" element={<FeatureGate flag="q_ai_dashboard_generator"><QAIDashboardHome /></FeatureGate>} />
                  <Route path="/qai-dashboards/new" element={<FeatureGate flag="q_ai_dashboard_generator"><QAIDashboardBuilder /></FeatureGate>} />
                  <Route path="/qai-dashboards/:id" element={<FeatureGate flag="q_ai_dashboard_generator"><QAIDashboardViewer /></FeatureGate>} />

                  {/* Delivery Management (flag-gated) */}
                  <Route path="/delivery/manifests" element={<FeatureGate flag="delivery_management"><DeliveryManifests /></FeatureGate>} />
                  <Route path="/delivery/manifests/new" element={<FeatureGate flag="delivery_management"><DeliveryManifestForm /></FeatureGate>} />
                  <Route path="/delivery/manifests/:id" element={<FeatureGate flag="delivery_management"><DeliveryManifestForm /></FeatureGate>} />
                  <Route path="/delivery/return-queue" element={<FeatureGate flag="delivery_management"><DeliveryReturnQueue /></FeatureGate>} />
                  {/* Top-level, not /delivery/*: a driver must not hold
                      nav.delivery (see routes/driverDeliveries.js) and every
                      /delivery/* path is gated on it. redirectTo breaks the
                      launch-page loop when the feature flag is off. */}
                  <Route path="/my-deliveries" element={<FeatureGate flag="delivery_management" redirectTo="/company-dashboard"><MyDeliveries /></FeatureGate>} />
                  <Route path="/delivery/drivers" element={<FeatureGate flag="delivery_management"><DeliveryDrivers /></FeatureGate>} />
                  <Route path="/delivery/vehicles" element={<FeatureGate flag="delivery_management"><DeliveryVehicles /></FeatureGate>} />
                  <Route path="/delivery/zones" element={<FeatureGate flag="delivery_management"><DeliveryZones /></FeatureGate>} />
                  <Route path="/delivery/return-reasons" element={<FeatureGate flag="delivery_management"><DeliveryReturnReasons /></FeatureGate>} />
                  <Route path="/delivery/reports/efficiency" element={<FeatureGate flag="delivery_management"><DeliveryEfficiency /></FeatureGate>} />
                  <Route path="/delivery/reports/manifest-throughput" element={<FeatureGate flag="delivery_management"><ManifestThroughputReport /></FeatureGate>} />
                  <Route path="/delivery/reports/driver-performance" element={<FeatureGate flag="delivery_management"><DriverPerformanceReport /></FeatureGate>} />
                  <Route path="/delivery/reports/return-analytics" element={<FeatureGate flag="delivery_management"><ReturnAnalyticsReport /></FeatureGate>} />
                  <Route path="/delivery/reports/sla-breach" element={<FeatureGate flag="delivery_management"><SlaBreachReport /></FeatureGate>} />
                  <Route path="/delivery/reports/zone-utilization" element={<FeatureGate flag="delivery_management"><ZoneUtilizationReport /></FeatureGate>} />
                  <Route path="/delivery/reports/reissue-cycle" element={<FeatureGate flag="delivery_management"><ReissueCycleReport /></FeatureGate>} />

                  {/* CRM (flag-gated) */}
                  <Route path="/crm/dashboard" element={<FeatureGate flag="crm_leads"><CrmDashboardHome /></FeatureGate>} />
                  <Route path="/crm/customer-360" element={<FeatureGate flag="crm_leads"><CrmCustomer360 /></FeatureGate>} />
                  <Route path="/crm/customer-360/:id" element={<FeatureGate flag="crm_leads"><CrmCustomer360 /></FeatureGate>} />
                  <Route path="/crm/reports/accounts-at-risk" element={<FeatureGate flag="crm_leads"><CrmAccountsAtRiskReport /></FeatureGate>} />
                  <Route path="/crm/contacts" element={<FeatureGate flag="crm_leads"><CrmContactsList /></FeatureGate>} />
                  <Route path="/crm/contacts/:id" element={<FeatureGate flag="crm_leads"><CrmContactDetail /></FeatureGate>} />
                  <Route path="/crm/opportunities" element={<FeatureGate flag="crm_leads"><CrmOpportunities /></FeatureGate>} />
                  <Route path="/crm/pipeline" element={<FeatureGate flag="crm_leads"><CrmSalesPipeline /></FeatureGate>} />
                  <Route path="/crm/pipeline-settings" element={<FeatureGate flag="crm_leads"><CrmPipelineSettings /></FeatureGate>} />
                  <Route path="/crm/workflows" element={<FeatureGate flag="crm_leads"><CrmWorkflowRules /></FeatureGate>} />
                  <Route path="/crm/discovery/templates" element={<FeatureGate flag="crm_leads"><CrmLeadsDiscoveryTemplates /></FeatureGate>} />
                  <Route path="/crm/discovery/queue" element={<FeatureGate flag="crm_leads"><CrmLeadReviewQueue /></FeatureGate>} />
                  <Route path="/crm/activities" element={<FeatureGate flag="crm_leads"><CrmActivitiesGlobal /></FeatureGate>} />
                  <Route path="/crm/opt-outs" element={<FeatureGate flag="crm_leads"><CrmOptOuts /></FeatureGate>} />
                  <Route path="/crm/mailbox" element={<FeatureGate flag="crm_leads"><CrmMailboxInbox /></FeatureGate>} />

                    <Route path="*" element={<Navigate to="/" />} />
                    </Routes>
                  </RbacRouteGate>
                </SuspenseWrapper>
              </MainLayout>
            </ProtectedRoute>
        }
      />
    </Routes>
  );
};

const App = () => {
  return (
    <Router>
      <ThemeProvider>
        <AuthProvider>
          <ConfirmDialogProvider>
            <ExpressMarketCartProvider>
              <ThemeScopeUser />
              <AppRoutes />
              <ThemedToaster />
              <SessionExpiredModal />
              <LicenseExpiredModal />
            </ExpressMarketCartProvider>
          </ConfirmDialogProvider>
        </AuthProvider>
      </ThemeProvider>
    </Router>
  );
};

export default App;
